"use client";

import type { ReactElement } from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import type { NodeProps } from "@xyflow/react";
import { GraphNodeFrame } from "../Cmarkdowngraphcontrols";
import { NODE_TEXT } from "./mindmap-constants";
import type { MindmapFlowNode } from "./mindmap-types";
import { MindmapInteractionContext } from "./mindmap-interaction-context";
import { CmarkdownLabelText } from "../Cmarkdowntext/CmarkdownLabelText";
import { MindmapCollapsedCountBadge } from "./MindmapCollapsedCountBadge";
import { MindmapNodeHandles } from "./MindmapNodeHandles";
import { getMindmapNodeModel } from "./mindmap-node-model";

export function MindmapNode({
  data,
  sourcePosition,
  targetPosition,
}: NodeProps<MindmapFlowNode>): ReactElement {
  const {
    onToggleExpand,
    onSelectNode,
    selectedNodeId,
    hoveredNodeId,
    highlightedNodeIds,
    isEntranceComplete,
  } = useContext(MindmapInteractionContext);
  const [isHovered, setIsHovered] = useState(false);
  const [didEntranceStart, setDidEntranceStart] =
    useState<boolean>(isEntranceComplete);

  const dataNodeId = data.nodeId;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let firstFrame: number | null = null;
    let secondFrame: number | null = null;

    if (isEntranceComplete) {
      firstFrame = window.requestAnimationFrame(() => {
        setDidEntranceStart(true);
      });

      return () => {
        if (firstFrame != null) {
          window.cancelAnimationFrame(firstFrame);
        }
      };
    }

    firstFrame = window.requestAnimationFrame(() => {
      setDidEntranceStart(false);
      secondFrame = window.requestAnimationFrame(() => {
        setDidEntranceStart(true);
      });
    });

    return () => {
      if (firstFrame != null) {
        window.cancelAnimationFrame(firstFrame);
      }
      if (secondFrame != null) {
        window.cancelAnimationFrame(secondFrame);
      }
    };
  }, [isEntranceComplete, dataNodeId]);

  const nodeModel = getMindmapNodeModel({
    data,
    sourcePosition,
    targetPosition,
    selectedNodeId,
    hoveredNodeId,
    highlightedNodeIds,
    didEntranceStart,
    isHovered,
    isEntranceComplete,
  });
  const { frame, label } = nodeModel;
  const hasChildren = nodeModel.hasChildren;

  const handleNodeClick = useCallback(() => {
    onSelectNode(dataNodeId);

    if (!hasChildren) {
      return;
    }

    onToggleExpand(dataNodeId);
  }, [dataNodeId, hasChildren, onSelectNode, onToggleExpand]);

  const nodeContent = (
    <>
      <CmarkdownLabelText
        dataElScope="Cmarkdownmindmap/MindmapNode"
        content={data.label}
        size={label.size}
        weight={label.weight}
        textAlign={label.textAlign}
        style={{ width: "100%", pointerEvents: "none" }}
        paragraphStyle={{ marginBottom: 0, justifyContent: "center", width: "100%" }}
        textStyle={{
          color: NODE_TEXT,
          lineHeight: label.lineHeight,
          whiteSpace: "nowrap",
        }}
      />

      {nodeModel.isCollapsed && (
        <MindmapCollapsedCountBadge childCount={data.childCount} />
      )}
    </>
  );

  return (
    <GraphNodeFrame
      shape={frame.shape}
      accentColor={frame.accentColor}
      fill={frame.fill}
      borderWidth={frame.borderWidth}
      isSelected={frame.isSelected}
      boxShadow={frame.boxShadow}
      opacity={frame.opacity}
      transform={frame.transform}
      transition={frame.transition}
      cursor={frame.cursor}
      contentPadding={frame.contentPadding}
      ariaLabel={data.label}
      onClick={handleNodeClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      handles={<MindmapNodeHandles handles={nodeModel.connectionHandles} />}
    >
      {nodeContent}
    </GraphNodeFrame>
  );
}
